import { Context } from '../../context/Context';
import { ArrayTexture } from '../../texture/ArrayTexture';
import { TerrainSurfaceRef } from '../SceneManifest';

/** Which PBR channel a surface PNG occupies. */
export type SurfaceChannel = 'albedo' | 'normal' | 'roughness';

/**
 * Resolves a surface's channel URL. Two implementations exist:
 *
 *  - Runtime (playful): returns `surfaces/{id}/{channel}.png` so the resource loader's
 *    asset-base prefix points the request at the published artifact.
 *  - Editor (asset-manager): returns `/api/projects/{projectId}/surfaces/{id}/{channel}`
 *    so authoring sees live unpublished bytes from the surface library.
 */
export type SurfaceUrlResolver = (surfaceId: string, channel: SurfaceChannel) => string;

/**
 * Fetches surface PNG bytes from a resolved URL. Defaults to the engine's resource
 * loader; the editor injects a direct `fetch()` so it can bypass the asset-base prefix
 * and hit the live `/api/...` endpoint.
 */
export type SurfaceFetch = (url: string) => Promise<Uint8Array>;

export interface SurfaceTextureManagerOptions {
  /** Texture dimension (square; backend normalizes uploads to 512²). */
  width?: number;
  height?: number;
  /**
   * Spare layers above `surfaces.length` reserved for surfaces the user paints during
   * an editor session that weren't in the original manifest. Runtime (playful) passes 0
   * — manifest surfaces are exhaustive. Editor passes ~16.
   */
  extraCapacity?: number;
  urlResolver: SurfaceUrlResolver;
  /** Optional override; defaults to `ctx.resourceLoader.loadResource`. */
  fetchImpl?: SurfaceFetch;
}

const DEFAULT_WIDTH = 512;
const DEFAULT_HEIGHT = 512;
/** Placeholder for un-loaded normal layers — points straight out (Y in tangent space). */
const NORMAL_PLACEHOLDER: [number, number, number] = [0.5, 0.5, 1.0];
/** Placeholder roughness; matches the canonical fallback in the surface props texture. */
const ROUGHNESS_PLACEHOLDER: [number, number, number] = [0.8, 0.8, 0.8];
/** Fallback roughness scalar written into surfacePropsTex when no roughness PNG is published. */
const ROUGHNESS_FALLBACK = 0.8;

/**
 * Owns the three terrain `ArrayTexture`s (albedo / normal / roughness), the
 * `surfacePropsTex` lookup, and lazy per-surface PNG fetching. Layers are
 * pre-allocated with solid placeholder colors so the renderer can sample correct
 * indices from the moment a tile installs — textures upgrade in place as PNGs
 * stream in via {@link acquire}.
 *
 * Reference-counted per-tile usage: tile load calls `acquire(surfaceId)` for each
 * surface its index map touches; tile eviction calls `release(surfaceId)`. v1
 * policy keeps a loaded layer cached even at refcount 0 (no LRU) — at N≤32 layers
 * × 512² × 4 bytes × 3 channels ≈ 36 MB this is fine; revisit if catalog sizes
 * grow past ~128 surfaces.
 */
export class SurfaceTextureManager {
  readonly albedoArray: ArrayTexture;
  readonly normalArray: ArrayTexture;
  readonly roughnessArray: ArrayTexture;
  readonly surfacePropsTex: WebGLTexture;
  readonly surfaceCount: number;

  private readonly ctx: Context;
  private readonly gl: WebGL2RenderingContext;
  private readonly width: number;
  private readonly height: number;
  private readonly urlResolver: SurfaceUrlResolver;
  private readonly fetchImpl: SurfaceFetch;

  /** Maps surfaceId → layer index. Empty string maps to layer 0 (placeholder slot). */
  private readonly layerByIdSurface: Map<string, number> = new Map();
  /** Mirror of the manifest entries by layer index. Editor mutates this on `registerSurface`. */
  private readonly entryByLayer: Array<TerrainSurfaceRef | null> = [];

  /** Active reference count per surfaceId. */
  private readonly refCounts: Map<string, number> = new Map();
  /** In-flight fetches by surfaceId — deduplicates concurrent acquires. */
  private readonly inflight: Map<string, Promise<void>> = new Map();
  /**
   * Surfaces that finished a fetch with at least one channel actually uploaded.
   * A surface stays NOT loaded when every channel fetch failed (so `acquire`
   * will retry on next call) and the `markDirty` callback in `fetchAndUpload`
   * never fired.
   */
  private readonly loaded: Set<string> = new Set();
  /**
   * Per-surface generation counter, bumped by {@link invalidate}. Fetches
   * capture the generation at start and discard their result (no `loaded.add`,
   * no `markDirty`) when a newer generation has been requested. Without this,
   * calling invalidate while a fetch is in flight would let the stale bytes
   * mark themselves authoritative and block the next refetch.
   */
  private readonly generation: Map<string, number> = new Map();

  private dirtyAlbedo = false;
  private dirtyNormal = false;
  private dirtyRoughness = false;
  private destroyed = false;

  /** Capacity (= `surfaces.length + extraCapacity`). Cached so `registerSurface` can range-check. */
  private readonly capacity: number;
  /** Next free layer index for editor-mode `registerSurface`. Starts at `surfaces.length`. */
  private nextFreeLayer: number;

  constructor(ctx: Context, surfaces: TerrainSurfaceRef[], opts: SurfaceTextureManagerOptions) {
    this.ctx = ctx;
    this.gl = ctx.gl;
    this.width = opts.width ?? DEFAULT_WIDTH;
    this.height = opts.height ?? DEFAULT_HEIGHT;
    this.urlResolver = opts.urlResolver;
    this.fetchImpl =
      opts.fetchImpl ?? ((url) => ctx.resourceLoader.loadResource(url));

    this.surfaceCount = surfaces.length;
    this.capacity = surfaces.length + Math.max(0, opts.extraCapacity ?? 0);
    this.nextFreeLayer = surfaces.length;

    // Populate the entry table + reverse index. Manifest entries occupy layers
    // 0..N-1; slack layers stay null until `registerSurface` claims them.
    for (let i = 0; i < this.capacity; i++) {
      const s: TerrainSurfaceRef | undefined = surfaces[i];
      this.entryByLayer.push(s ?? null);
      if (s && s.surfaceId.length > 0) {
        this.layerByIdSurface.set(s.surfaceId, i);
      }
    }
    // Layer 0 is always the placeholder slot; ensure the empty-string id maps
    // there so `getLayerIndex(undefined)` and unknown ids fall back to layer 0.
    if (!this.layerByIdSurface.has('')) {
      this.layerByIdSurface.set('', 0);
    }

    const albedoColorAt = (i: number): readonly [number, number, number] => {
      const s = this.entryByLayer[i];
      return s ? [s.color[0] ?? 0.5, s.color[1] ?? 0.5, s.color[2] ?? 0.5] : [0.5, 0.5, 0.5];
    };
    const normalColorAt = () => NORMAL_PLACEHOLDER;
    const roughnessColorAt = () => ROUGHNESS_PLACEHOLDER;

    this.albedoArray = ArrayTexture.withCapacity(
      ctx, this.capacity, this.width, this.height,
      { srgb: true, mipmaps: true },
      albedoColorAt,
    );
    this.normalArray = ArrayTexture.withCapacity(
      ctx, this.capacity, this.width, this.height,
      { srgb: false, mipmaps: true },
      normalColorAt,
    );
    this.roughnessArray = ArrayTexture.withCapacity(
      ctx, this.capacity, this.width, this.height,
      { srgb: false, mipmaps: true },
      roughnessColorAt,
    );

    this.surfacePropsTex = buildSurfacePropsTex(
      this.gl,
      this.entryByLayer.map((s) => (s ? s.tilingScale : 1)),
      this.entryByLayer.map(() => ROUGHNESS_FALLBACK),
    );
  }

  /** Returns the layer index for a surfaceId, or 0 (placeholder) for unknown/empty. */
  getLayerIndex(surfaceId: string | undefined | null): number {
    if (!surfaceId) return 0;
    return this.layerByIdSurface.get(surfaceId) ?? 0;
  }

  /** All currently-known surfaceIds (manifest + editor-registered). */
  knownSurfaceIds(): string[] {
    const out: string[] = [];
    for (const id of this.layerByIdSurface.keys()) {
      if (id.length > 0) out.push(id);
    }
    return out;
  }

  /**
   * Increment the refcount for [surfaceId] and start fetching its channels if the
   * layer hasn't been populated yet. Returns the layer index.
   *
   * Refcount semantics:
   *  - First reference triggers fetch (deduped via {@link inflight}).
   *  - Subsequent references during fetch wait on the same promise but return immediately.
   *  - {@link release} decrements; eviction is a no-op in v1 (keep cached).
   */
  acquire(surfaceId: string): number {
    const layerIdx = this.layerByIdSurface.get(surfaceId);
    if (layerIdx === undefined) {
      // Caller painted with a surface that wasn't registered. Default to placeholder
      // layer 0 so rendering stays consistent; the editor should have called
      // registerSurface() first. Log once per offending id.
      if (!this.unknownWarned.has(surfaceId)) {
        this.unknownWarned.add(surfaceId);
        console.warn(`[SurfaceTextureManager] acquire(${surfaceId}) — unknown surface, using layer 0 placeholder`);
      }
      return 0;
    }
    const prev = this.refCounts.get(surfaceId) ?? 0;
    this.refCounts.set(surfaceId, prev + 1);

    if (this.loaded.has(surfaceId)) return layerIdx;
    if (this.inflight.has(surfaceId)) return layerIdx;

    const entry = this.entryByLayer[layerIdx];
    if (!entry) return layerIdx; // unreachable — layer index came from entry list
    this.startFetch(surfaceId, layerIdx, entry);
    return layerIdx;
  }

  private readonly unknownWarned = new Set<string>();

  /** Decrement the refcount for [surfaceId]. v1: layer stays cached at zero. */
  release(surfaceId: string): void {
    const prev = this.refCounts.get(surfaceId) ?? 0;
    if (prev <= 1) {
      this.refCounts.delete(surfaceId);
    } else {
      this.refCounts.set(surfaceId, prev - 1);
    }
  }

  /**
   * Editor-only: force a refetch of this surface's channels regardless of cache state.
   * Used when {@link SurfaceCatalogService.surfaceChanged$} fires — the bytes on the
   * server changed but the layer index is stable.
   */
  invalidate(surfaceId: string): void {
    const layerIdx = this.layerByIdSurface.get(surfaceId);
    if (layerIdx === undefined) return;
    const entry = this.entryByLayer[layerIdx];
    if (!entry) return;
    // Bump the generation. Any in-flight fetch captured the old generation at
    // startFetch and will discard its result on completion. The next line clears
    // `loaded` and `inflight` so this `startFetch` can run unconditionally.
    this.generation.set(surfaceId, (this.generation.get(surfaceId) ?? 0) + 1);
    this.loaded.delete(surfaceId);
    this.inflight.delete(surfaceId);
    this.startFetch(surfaceId, layerIdx, entry);
  }

  /**
   * Editor-only: claim a spare layer for a surface that wasn't in the original
   * manifest (the user painted with a brand-new surface mid-session). Throws if no
   * spare capacity remains — UX should hard-cap at the slack count and surface a
   * "rebuild terrain to add more surfaces" message.
   */
  registerSurface(surface: TerrainSurfaceRef): number {
    if (this.layerByIdSurface.has(surface.surfaceId)) {
      return this.layerByIdSurface.get(surface.surfaceId)!;
    }
    if (this.nextFreeLayer >= this.capacity) {
      throw new Error(
        `SurfaceTextureManager: out of spare capacity (${this.capacity - this.surfaceCount} editor slots used). ` +
          `Rebuild terrain to expand the layer array.`,
      );
    }
    const layerIdx = this.nextFreeLayer++;
    this.entryByLayer[layerIdx] = surface;
    this.layerByIdSurface.set(surface.surfaceId, layerIdx);

    // Update the placeholder color so unfetched samples render the authored color.
    const scratch = new Uint8Array(this.width * this.height * 4);
    const r8 = clamp255(surface.color[0]);
    const g8 = clamp255(surface.color[1]);
    const b8 = clamp255(surface.color[2]);
    for (let p = 0; p < scratch.length; p += 4) {
      scratch[p] = r8;
      scratch[p + 1] = g8;
      scratch[p + 2] = b8;
      scratch[p + 3] = 255;
    }
    this.albedoArray.updateLayer(this.gl, layerIdx, scratch, this.width, this.height);
    this.dirtyAlbedo = true;

    // Update surfacePropsTex's tiling slot for this layer (roughness fallback unchanged).
    updateSurfacePropsTexel(this.gl, this.surfacePropsTex, layerIdx, surface.tilingScale, ROUGHNESS_FALLBACK);
    return layerIdx;
  }

  /**
   * Regenerate mipmaps for any array dirtied since the last flush. Called once per
   * frame from `TiledTerrainStreamer.tick()`. Cheap when no layer fills happened.
   */
  flushMipmaps(): void {
    if (this.destroyed) return;
    if (this.dirtyAlbedo) {
      this.albedoArray.regenerateMipmaps(this.gl);
      this.dirtyAlbedo = false;
    }
    if (this.dirtyNormal) {
      this.normalArray.regenerateMipmaps(this.gl);
      this.dirtyNormal = false;
    }
    if (this.dirtyRoughness) {
      this.roughnessArray.regenerateMipmaps(this.gl);
      this.dirtyRoughness = false;
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.albedoArray.glTexture) this.gl.deleteTexture(this.albedoArray.glTexture);
    if (this.normalArray.glTexture) this.gl.deleteTexture(this.normalArray.glTexture);
    if (this.roughnessArray.glTexture) this.gl.deleteTexture(this.roughnessArray.glTexture);
    this.gl.deleteTexture(this.surfacePropsTex);
  }

  private startFetch(surfaceId: string, layerIdx: number, entry: TerrainSurfaceRef): void {
    // Snapshot the generation so a concurrent `invalidate` can supersede us:
    // the bump happens in invalidate(), the comparison happens on completion.
    const gen = this.generation.get(surfaceId) ?? 0;
    const promise = (async () => {
      let anySucceeded = false;
      const tasks: Promise<void>[] = [];
      if (entry.hasAlbedo) {
        tasks.push(
          this.fetchAndUpload(surfaceId, gen, 'albedo', layerIdx, this.albedoArray, () => {
            this.dirtyAlbedo = true;
            anySucceeded = true;
          }),
        );
      }
      if (entry.hasNormal) {
        tasks.push(
          this.fetchAndUpload(surfaceId, gen, 'normal', layerIdx, this.normalArray, () => {
            this.dirtyNormal = true;
            anySucceeded = true;
          }),
        );
      }
      if (entry.hasRoughness) {
        tasks.push(
          this.fetchAndUpload(surfaceId, gen, 'roughness', layerIdx, this.roughnessArray, () => {
            this.dirtyRoughness = true;
            anySucceeded = true;
          }),
        );
      }
      await Promise.all(tasks);
      if (this.destroyed) return;
      // Don't mark loaded when a newer invalidate has superseded us — the next
      // acquire/invalidate must trigger a fresh fetch.
      if ((this.generation.get(surfaceId) ?? 0) !== gen) return;
      // Don't mark loaded when every channel fetch failed; otherwise the
      // surface would never retry on a future acquire.
      if (!anySucceeded) return;
      this.loaded.add(surfaceId);
    })();
    this.inflight.set(surfaceId, promise);
    promise.finally(() => {
      // Only clear the inflight slot if it's still ours — invalidate may have
      // already swapped in a newer promise.
      if (this.inflight.get(surfaceId) === promise) {
        this.inflight.delete(surfaceId);
      }
    });
  }

  private async fetchAndUpload(
    surfaceId: string,
    gen: number,
    channel: SurfaceChannel,
    layerIdx: number,
    target: ArrayTexture,
    markDirty: () => void,
  ): Promise<void> {
    try {
      const url = this.urlResolver(surfaceId, channel);
      const bytes = await this.fetchImpl(url);
      if (this.destroyed) return;
      if ((this.generation.get(surfaceId) ?? 0) !== gen) return;
      const img = await this.ctx.resourceLoader.loadImageData(bytes, 'image/png');
      if (this.destroyed) return;
      if ((this.generation.get(surfaceId) ?? 0) !== gen) return;
      if (img.width !== this.width || img.height !== this.height) {
        console.warn(
          `[SurfaceTextureManager] ${surfaceId}/${channel} is ${img.width}x${img.height}, ` +
            `expected ${this.width}x${this.height}; skipping layer update`,
        );
        return;
      }
      target.updateLayer(this.gl, layerIdx, img.data, img.width, img.height);
      markDirty();
    } catch (e) {
      console.warn(`[SurfaceTextureManager] ${surfaceId}/${channel} fetch failed; keeping placeholder`, e);
    }
  }
}

function clamp255(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}

/**
 * Build a 1×N RGBA32F texture holding `(tilingScale, roughnessFallback, 0, 0)` per
 * layer. Sampled by the terrain shader via `texelFetch` to recover per-surface
 * tiling and roughness without an extra uniform array.
 */
function buildSurfacePropsTex(
  gl: WebGL2RenderingContext,
  tilings: number[],
  roughnessFallbacks: number[],
): WebGLTexture {
  const n = tilings.length;
  const data = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    data[i * 4 + 0] = tilings[i];
    data[i * 4 + 1] = roughnessFallbacks[i];
  }
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA32F, n, 1);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, n, 1, gl.RGBA, gl.FLOAT, data);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

/** Patch one layer's (tiling, roughnessFallback) entry in the props texture. */
function updateSurfacePropsTexel(
  gl: WebGL2RenderingContext,
  tex: WebGLTexture,
  layerIdx: number,
  tilingScale: number,
  roughnessFallback: number,
): void {
  const data = new Float32Array(4);
  data[0] = tilingScale;
  data[1] = roughnessFallback;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, layerIdx, 0, 1, 1, gl.RGBA, gl.FLOAT, data);
  gl.bindTexture(gl.TEXTURE_2D, null);
}
